"use client";

import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/components/table";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, Fragment, useState } from "react";
import { Link } from "react-router-dom";
import ListLoader from "components/list/loader";
import SortableHeader from "./sortable-header";
import classNames from "classnames";
import Icon from "../icon";
// import { CHEVRON_DOWN, CHEVRON_RIGHT } from "icons";
import { UP, DOWN } from "icons";
import { Button } from "ui/index";
import { usePermissions } from "contexts/permission";
import { Checkbox } from "ui/components/checkbox";

export default function CommonTable({
  data = [],
  columns = [],
  onSort,
  href,
  onClick,
  linkProps,
  loader,
  rows,
  totals,
  showHeader = true,
  rowClassName = "h-10",
  getRowClassName, // Optional fn: (rowOriginal) => string — for dynamic per-row classes
  sortBy,
  sortOrder,
  setState = () => { },
  endPoint,
  getSubRows, // Function to extract sub-rows from a row (e.g., row => row.employees)
  enableAccordion = false, // Enable accordion functionality
  renderExpandedRow, // Function to render content when row is expanded: (row) => ReactNode
  expandedRowClassName = "", // Additional className for expanded row content
  selection = false,
  rowSelection = {},
  setRowSelection = () => { },
}) {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const permissions = usePermissions();

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const currentSorting = useMemo(() => {
    if (sortBy) {
      return [
        {
          id: sortBy,
          desc: sortOrder === "desc",
        },
      ];
    }
    return [];
  }, [sortBy, sortOrder]);

  const tableColumns = useMemo(() => {
    const permittedColumns = columns.filter((col) => {
      const fieldName = col?.field;
      if (endPoint && fieldName) {
        return permissions.hasFieldAccess(endPoint, "read", fieldName);
      }
      return true;
    });

    let finalColumns = permittedColumns;

    if (selection) {
      finalColumns = [
        {
          id: "_select",
          header: ({ table }) => (
            <Checkbox
              checked={
                table.getIsAllPageRowsSelected() ||
                (table.getIsSomePageRowsSelected() && "indeterminate")
              }
              onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label="Select all"
            />
          ),
          cell: ({ row }) => (
            <div className="px-2">
              <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(value) => row.toggleSelected(!!value)}
                aria-label="Select row"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          ),
          size: 40,
        },
        ...finalColumns,
      ];
    }

    if (!enableAccordion) return finalColumns;

    return [
      ...finalColumns,
      {
        id: "_expand",
        header: "",
        size: 40,
        cell: ({ row }) => (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              toggleRowExpansion(row.id);
            }}
            variant="ghost"
            className={classNames(
              "p-1 hover:bg-muted rounded transition-colors invisible group-hover:visible",
              expandedRows.has(row.id) && "visible",
            )}
          >
            <Icon
              icon={DOWN}
              className={classNames(
                "h-4 w-4 transition-transform duration-200",
                expandedRows.has(row.id) && "rotate-180",
              )}
            />
          </Button>
        ),
      },
    ];
  }, [enableAccordion, columns, expandedRows, endPoint, permissions]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
    manualSorting: true,
    getCanSort: (column) => column.columnDef.sortable === true,
    state: {
      sorting: currentSorting,
      rowSelection,
    },
    enableRowSelection: selection,
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row.id,
  });

  const hasFooter = tableColumns.some((col) => col?.footer);

  if (loader?.isLoading)
    return <ListLoader columns={tableColumns} rows={rows} />;

  return (
    <div className="border-b">
      <Table>
        {showHeader && (
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="text-muted-foreground font-medium"
                    style={{ width: header.column.columnDef.size }}
                  // style={{
                  //   maxWidth: header.getSize(),
                  // }}
                  >
                    <SortableHeader column={header.column} onSort={onSort}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                    </SortableHeader>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
        )}
        <TableBody>
          {table.getRowModel().rows.map((row) => {
            const subRows = getSubRows ? getSubRows(row.original) : null;
            const hasSubRows = subRows && subRows.length > 0;
            const subRowCount = hasSubRows ? subRows.length : 1;

            // Helper to render cell content
            const renderCellContent = (cell, subRowData = null) => {
              const isSubRowColumn =
                cell.column.columnDef.meta?.subRowAccessor !== undefined;

              // For sub-row columns, override getValue to return the subRowData directly
              const context =
                subRowData && isSubRowColumn
                  ? {
                    ...cell.getContext(),
                    row: { ...cell.row, original: subRowData },
                    getValue: () => subRowData,
                  }
                  : cell.getContext();

              const content = flexRender(cell.column.columnDef.cell, context);

              // 1. Check for column-level href
              const colHref = cell.column.columnDef.href;
              const evaluatedColHref = colHref !== undefined
                ? (typeof colHref === "function" ? colHref(subRowData || row.original) : colHref)
                : undefined;

              // 2. Check for row-level href
              const rowHref = href
                ? (typeof href === "function" ? href(subRowData || row.original) : href)
                : undefined;

              // 3. Determine the final active href and props (Column overrides Row)
              // evaluatedColHref === false means column explicitly opts out of linking
              const activeHref = evaluatedColHref === false ? null : (evaluatedColHref || rowHref);
              const activeLinkProps = evaluatedColHref ? (cell.column.columnDef.linkProps || {}) : (linkProps || {});

              if (activeHref) {
                return (
                  <Link
                    to={activeHref}
                    state={setState(subRowData || row.original)}
                    {...activeLinkProps}
                    className="block p-2"
                    onClick={(e) => {
                      if (onClick) onClick(subRowData || row.original);
                    }}
                  >
                    {content}
                  </Link>
                );
              }

              if (onClick) {
                return (
                  <div
                    onClick={() => onClick(subRowData || row.original)}
                    className="block p-2 cursor-pointer"
                  >
                    {content}
                  </div>
                );
              }

              if (href || onClick) {
                return <div className="block p-2">{content}</div>;
              }

              return content;
            };

            if (!hasSubRows) {
              // Normal row rendering (no sub-rows)
              const isExpanded = expandedRows.has(row.id);

              const dynamicClass = getRowClassName ? getRowClassName(row.original) : "";
              return (
                <Fragment key={row.id}>
                  <TableRow
                    className={` cursor-pointer group${rowClassName ? ` ${rowClassName}` : ""}${dynamicClass ? ` ${dynamicClass}` : ""}`}
                    onClick={() => {
                      if (selection) row.toggleSelected();
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={classNames(
                          { "p-0": href || onClick },
                          "whitespace-pre-wrap cursor-pointer",
                        )}
                      >
                        {renderCellContent(cell)}
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Render expanded row content */}
                  {enableAccordion && isExpanded && renderExpandedRow && (
                    <TableRow className={expandedRowClassName}>
                      <TableCell
                        colSpan={tableColumns.length}
                        className="cursor-pointer"
                      >
                        <div className="p-4">
                          {renderExpandedRow(row.original)}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            }

            // Row with sub-rows: render first row + additional sub-rows
            const dynamicClassSubRow = getRowClassName ? getRowClassName(row.original) : "";
            return (
              <Fragment key={row.id}>
                {/* First row - includes all columns, with rowSpan for parent columns */}
                <TableRow
                  className={`group${rowClassName ? ` ${rowClassName}` : ""}${dynamicClassSubRow ? ` ${dynamicClassSubRow}` : ""}`}
                  onClick={() => {
                    if (selection) row.toggleSelected();
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta || {};
                    const shouldSpan = meta.spanRow === true;
                    const isSubRowColumn = meta.subRowAccessor !== undefined;

                    return (
                      <TableCell
                        key={cell.id}
                        rowSpan={shouldSpan ? subRowCount : undefined}
                        className={classNames(
                          { "p-0": href || onClick },
                          "whitespace-pre-wrap cursor-pointer",
                        )}
                      >
                        {isSubRowColumn
                          ? renderCellContent(cell, subRows[0])
                          : renderCellContent(cell)}
                      </TableCell>
                    );
                  })}
                </TableRow>

                {/* Additional sub-rows - only render columns without spanRow */}
                {subRows.slice(1).map((subRowData, subIndex) => (
                  <TableRow
                    key={`${row.id}-sub-${subIndex + 1}`}
                    className={`group${rowClassName ? ` ${rowClassName}` : ""}${dynamicClassSubRow ? ` ${dynamicClassSubRow}` : ""}`}
                    onClick={() => {
                      if (selection) row.toggleSelected();
                    }}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const meta = cell.column.columnDef.meta || {};
                      const shouldSpan = meta.spanRow === true;
                      const isSubRowColumn = meta.subRowAccessor !== undefined;

                      // Skip cells that are spanning from the first row
                      if (shouldSpan) return null;

                      return (
                        <TableCell
                          key={cell.id}
                          className={classNames(
                            { "p-0": href || onClick },
                            "whitespace-pre-wrap",
                          )}
                        >
                          {isSubRowColumn
                            ? renderCellContent(cell, subRowData)
                            : null}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </Fragment>
            );
          })}
        </TableBody>
        {hasFooter && totals && (
          <TableFooter>
            <TableRow className="bg-muted/50 font-medium">
              {table.getHeaderGroups()[0].headers.map((header) => {
                const columnDef = header.column.columnDef;
                const footerContent = columnDef.footer
                  ? typeof columnDef.footer === "function"
                    ? columnDef.footer(totals)
                    : columnDef.footer
                  : "";

                return (
                  <TableCell key={header.id} className="font-semibold">
                    {footerContent}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </div>
  );
}
